import { ReactiveController, ReactiveControllerHost } from "lit";
import { AppState, RatingConfig } from "../models";
import { encodeRatings, decodeRatings } from "../utils/serialization";
import { saveToLocal, loadFromLocal } from "../utils/storage";
import { DATASET_COLORS } from "../constants";

/**
 * Controller for managing AppState persistence (URL hash & LocalStorage).
 */
export class PersistenceController implements ReactiveController {
  state!: AppState;

  private _isUpdatingInternally = false;

  constructor(
    private host: ReactiveControllerHost,
    private config: RatingConfig
  ) {
    host.addController(this);
    this.state = this._loadInitialState();
  }

  hostConnected() {
    window.addEventListener("hashchange", this._onHashChange);
  }

  hostDisconnected() {
    window.removeEventListener("hashchange", this._onHashChange);
  }

  private _onHashChange = () => {
    if (this._isUpdatingInternally) return;
    this.state = this._loadInitialState();
    this.host.requestUpdate();
  };

  private _loadInitialState(): AppState {
    const hash = window.location.hash;
    if (hash) {
      return decodeRatings(hash, this.config);
    }
    
    const local = loadFromLocal();
    if (local && local.datasets && local.datasets.length > 0) {
        return local;
    }

    const defaultRatings: Record<string, number> = {};
    for (const attr of this.config.attributes) {
      defaultRatings[attr.id] = 0;
    }
    return { 
      datasets: [{ ratings: defaultRatings, color: DATASET_COLORS[0] }],
      theme: "system" 
    };
  }

  update(newState: AppState) {
    this._isUpdatingInternally = true;
    try {
      this.state = newState;
      const hash = encodeRatings(this.state, this.config);
      history.replaceState(null, "", hash);
      saveToLocal(this.state);
      this.host.requestUpdate();
    } finally {
      // Small delay to allow hashchange event to fire and be ignored if necessary,
      // although history.replaceState doesn't trigger hashchange.
      // We keep it robust.
      setTimeout(() => {
        this._isUpdatingInternally = false;
      }, 0);
    }
  }
}
